import { useState } from "react";
import { BookOpen, Wrench, AlertCircle, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const SEVERITY_TONE = {
    critical: { c: "#DC2626", bg: "#FEE2E2", b: "#FCA5A5", label: "CRITICAL" },
    warning: { c: "#B45309", bg: "#FEF3C7", b: "#FCD34D", label: "WARNING" },
    info: { c: "#1D4ED8", bg: "#DBEAFE", b: "#93C5FD", label: "INFO" },
};

/**
 * Manual-based technical guide for a specific alert / fault code.
 *
 * Props:
 *   guide        — the `technical_guide` payload from the backend
 *   compact      — when true, renders as a collapsed "Technical Guide" chip
 *                  that expands inline (used inside a table row)
 *   testid
 */
export default function TechnicalGuide({ guide, compact = false, testid = "tech-guide" }) {
    const [open, setOpen] = useState(!compact);

    if (!guide) {
        if (compact) return null;
        return (
            <div className="text-xs italic text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-sm p-3" data-testid={`${testid}-empty`}>
                Manual reference not found for this alert. Add the fault code to <code className="font-mono">alert_guide.py</code> to link it.
            </div>
        );
    }

    const tone = SEVERITY_TONE[guide.severity] || SEVERITY_TONE.warning;

    if (compact) {
        return (
            <div className="mt-2 rounded-sm border" style={{ borderColor: tone.b, background: `${tone.bg}55` }} data-testid={testid}>
                <button
                    onClick={() => setOpen(o => !o)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium"
                    style={{ color: tone.c }}
                    data-testid={`${testid}-toggle`}
                >
                    <BookOpen className="w-3.5 h-3.5" />
                    <span>Technical Guide · <b className="font-mono">{guide.code}</b></span>
                    <span className="text-zinc-500 font-normal">— {guide.title}</span>
                    {open ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
                </button>
                {open && (
                    <div className="px-3 pb-3 pt-1 space-y-2">
                        <div className="flex items-start gap-1.5 text-xs">
                            <AlertCircle className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0 mt-0.5" />
                            <span className="text-zinc-700 leading-relaxed">{guide.cause}</span>
                        </div>
                        <div className="text-xs">
                            <div className="flex items-center gap-1.5 text-zinc-500 font-semibold uppercase tracking-widest text-[10px] mb-1.5">
                                <Wrench className="w-3 h-3" /> Troubleshooting Steps
                            </div>
                            <ol className="list-decimal list-inside space-y-1 text-zinc-700 leading-relaxed" data-testid={`${testid}-steps`}>
                                {guide.troubleshooting.map((step, i) => <li key={i}>{step}</li>)}
                            </ol>
                        </div>
                        <div className="text-[10px] font-mono text-zinc-400 pt-1 border-t border-zinc-200 flex items-center gap-1">
                            <ExternalLink className="w-2.5 h-2.5" /> Source: {guide.source}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Full expanded card (AlertDetail page)
    return (
        <div
            className="rounded-sm border overflow-hidden"
            style={{ borderColor: tone.b, background: `${tone.bg}33` }}
            data-testid={testid}
        >
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: tone.b }}>
                <BookOpen className="w-4 h-4" style={{ color: tone.c }} />
                <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: tone.c }}>Technical Guide from Inverter Manual</div>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-mono text-sm font-semibold text-zinc-900" data-testid={`${testid}-code`}>{guide.code}</span>
                        <span className="text-zinc-400">·</span>
                        <span className="text-sm text-zinc-800" data-testid={`${testid}-title`}>{guide.title}</span>
                        <span className="ml-2 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border"
                            style={{ color: tone.c, borderColor: tone.b, background: "#FFFFFF" }}>
                            {tone.label}
                        </span>
                    </div>
                </div>
            </div>
            <div className="p-4 space-y-4 bg-white/70">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Cause
                    </div>
                    <div className="text-sm text-zinc-800 leading-relaxed" data-testid={`${testid}-cause`}>{guide.cause}</div>
                </div>
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1">
                        <Wrench className="w-3 h-3" /> Troubleshooting Steps
                    </div>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-800 leading-relaxed" data-testid={`${testid}-steps`}>
                        {guide.troubleshooting.map((step, i) => <li key={i}><span className="ml-1">{step}</span></li>)}
                    </ol>
                </div>
                <div className="text-[11px] font-mono text-zinc-500 pt-2 border-t border-zinc-200 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Source: {guide.source}
                </div>
            </div>
        </div>
    );
}
